import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import sharedStyles from './AdminShared.module.scss';
import styles from './AdminAnalytics.module.scss';
import GeoPointMap from '../../ui/GeoPointMap';
import { GeoPointMarker } from '../../ui/GeoPointMapModel';
import AutoRefreshControl from './components/AutoRefreshControl';
import PlaybackPerformancePanel from './components/PlaybackPerformancePanel';
import {
    AdvertisingConversionSummary,
    AnalyticsAudience,
    AnalyticsCampaignCount,
    AnalyticsDailyCount,
    AnalyticsDeviceBucket,
    AnalyticsEventTypeCount,
    AnalyticsLabelCount,
    AnalyticsLocationBucket,
    AnalyticsOverview,
    AnalyticsRecentActivity,
    AnalyticsUserGrowth,
    AnalyticsUserGrowthMonthlyBucket,
    AnalyticsVideoCount,
    SubscriptionFunnelAnalytics
} from '../../../types/analytics.types';
import { useAnalyticsService } from '../../../services/analytics.service';
import { useAutoRefresh } from '../../../hooks/useAutoRefresh';

const dayOptions = [7, 30, 90] as const;
const topOptions = [5, 10, 20] as const;
const monthOptions = [6, 12, 24, 36] as const;

type AnalyticsTab = 'overview' | 'users' | 'video' | 'playback' | 'discovery' | 'retention' | 'advertising' | 'activity' | 'logins' | 'audience';

const tabs: { id: AnalyticsTab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'users', label: 'Users' },
    { id: 'video', label: 'Video Activity' },
    { id: 'playback', label: 'Playback' },
    { id: 'discovery', label: 'Discovery' },
    { id: 'retention', label: 'Retention' },
    { id: 'advertising', label: 'Advertising' },
    { id: 'activity', label: 'Activity' },
    { id: 'logins', label: 'Logins' },
    { id: 'audience', label: 'Audience' }
];

const formatShortDate = (value: string) => {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return value;
    }

    return parsed.toLocaleDateString();
};

const formatMonth = (value: string) => {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return value;
    }

    return parsed.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
};

const formatDateTime = (value: string) => {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return value;
    }

    return parsed.toLocaleString();
};

const formatUtcShortDate = (value: string) => {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return value;
    }

    return parsed.toLocaleDateString(undefined, { timeZone: 'UTC' });
};

const formatNumber = (value: number) => value.toLocaleString();

const formatEventType = (value: string) => value
    .split('_')
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(' ');

const buildMax = (items: AnalyticsDailyCount[]) => items.reduce((max, item) => Math.max(max, item.count), 0);

const buildTopMax = (items: AnalyticsVideoCount[]) => items.reduce((max, item) => Math.max(max, item.count), 0);

const buildGrowthMax = (items: AnalyticsUserGrowthMonthlyBucket[]) =>
    items.reduce((max, item) => Math.max(max, item.newUsers), 0);

const buildGrowthTooltip = (month: string, label: string, value: number, total: number) =>
    `${month}: ${label} ${formatNumber(value)} of ${formatNumber(total)} new users`;

const toDateInputValue = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const buildDefaultFromDate = () => {
    const value = new Date();
    value.setDate(value.getDate() - 29);
    return toDateInputValue(value);
};

const buildLocationLabel = (location: AnalyticsLocationBucket) =>
    Array.from(new Set([location.city, location.regionName, location.countryName].filter(Boolean))).join(', ');

const buildDeviceBucketKey = (device: AnalyticsDeviceBucket, includeBrowserVersion: boolean, index: number) =>
    [
        device.deviceType,
        device.operatingSystem,
        device.operatingSystemVersion ?? 'unknown-os-version',
        device.browser,
        includeBrowserVersion ? device.browserVersion ?? 'unknown-browser-version' : 'all-browser-versions',
        index.toString()
    ].join('|');

const buildAudienceRangeLabel = (audienceData: AnalyticsAudience) => {
    const exclusiveEnd = new Date(audienceData.rangeEndUtc);
    if (Number.isNaN(exclusiveEnd.getTime())) {
        return `${formatUtcShortDate(audienceData.rangeStartUtc)} - ${formatUtcShortDate(audienceData.rangeEndUtc)}`;
    }

    // Backend returns an exclusive end date so clamped ranges are labelled as the user sees them.
    exclusiveEnd.setUTCDate(exclusiveEnd.getUTCDate() - 1);
    return `${formatUtcShortDate(audienceData.rangeStartUtc)} - ${formatUtcShortDate(exclusiveEnd.toISOString())}`;
};

interface AudienceMapLocation {
    key: string;
    label: string;
    latitude: number | null;
    longitude: number | null;
    count: number;
    uniqueUsers: number;
    sourceCount: number;
}

const hasCoordinates = (location: Pick<AudienceMapLocation, 'latitude' | 'longitude'>) =>
    location.latitude !== null && location.longitude !== null;

const buildAudienceMapLocationKey = (label: string) => label.trim().toLocaleLowerCase();

const buildAudienceMapLocations = (locations: AnalyticsLocationBucket[]): AudienceMapLocation[] => {
    const locationMap = new Map<string, AudienceMapLocation>();

    locations.forEach((location) => {
        const label = buildLocationLabel(location) || location.countryName || 'Unknown location';
        const key = buildAudienceMapLocationKey(label);
        const existing = locationMap.get(key);

        if (existing) {
            existing.count += location.count;
            existing.uniqueUsers += location.uniqueUsers;
            existing.sourceCount += 1;

            if (!hasCoordinates(existing) && hasCoordinates(location)) {
                existing.latitude = location.latitude;
                existing.longitude = location.longitude;
            }

            return;
        }

        locationMap.set(key, {
            key,
            label,
            latitude: location.latitude,
            longitude: location.longitude,
            count: location.count,
            uniqueUsers: location.uniqueUsers,
            sourceCount: 1
        });
    });

    return Array.from(locationMap.values()).sort((first, second) =>
        second.count - first.count || first.label.localeCompare(second.label)
    );
};

const AdminAnalytics = () => {
    const { fetchOverview, fetchUserGrowth, fetchSubscriptionFunnel, fetchAudience } = useAnalyticsService();
    const [activeTab, setActiveTab] = useState<AnalyticsTab>('overview');
    const [overview, setOverview] = useState<AnalyticsOverview | null>(null);
    const [userGrowth, setUserGrowth] = useState<AnalyticsUserGrowth | null>(null);
    const [subscriptionFunnel, setSubscriptionFunnel] = useState<SubscriptionFunnelAnalytics | null>(null);
    const [audience, setAudience] = useState<AnalyticsAudience | null>(null);
    const [days, setDays] = useState<number>(30);
    const [top, setTop] = useState<number>(10);
    const [months, setMonths] = useState<number>(12);
    const [audienceFrom, setAudienceFrom] = useState<string>(buildDefaultFromDate);
    const [audienceTo, setAudienceTo] = useState<string>(() => toDateInputValue(new Date()));
    const [includeAdministratorActivity, setIncludeAdministratorActivity] = useState<boolean>(false);
    const [includeBrowserVersion, setIncludeBrowserVersion] = useState<boolean>(false);
    const [isOverviewLoading, setIsOverviewLoading] = useState(true);
    const [isUserGrowthLoading, setIsUserGrowthLoading] = useState(false);
    const [isSubscriptionFunnelLoading, setIsSubscriptionFunnelLoading] = useState(false);
    const [isAudienceLoading, setIsAudienceLoading] = useState(false);
    const [overviewError, setOverviewError] = useState<string | null>(null);
    const [userGrowthError, setUserGrowthError] = useState<string | null>(null);
    const [subscriptionFunnelError, setSubscriptionFunnelError] = useState<string | null>(null);
    const [audienceError, setAudienceError] = useState<string | null>(null);
    const [activeGrowthTooltip, setActiveGrowthTooltip] = useState<string | null>(null);
    const [selectedAudienceLocationKeys, setSelectedAudienceLocationKeys] = useState<string[]>([]);
    const [autoRefresh, setAutoRefresh] = useState(false);
    const subscriptionFunnelRequestRef = useRef(0);

    const loadOverview = useCallback(async () => {
        setIsOverviewLoading(true);
        setOverviewError(null);
        try {
            const response = await fetchOverview(days, top, !includeAdministratorActivity);
            setOverview(response);
        } catch {
            setOverviewError('Unable to load analytics.');
        } finally {
            setIsOverviewLoading(false);
        }
    }, [days, top, includeAdministratorActivity, fetchOverview]);

    useEffect(() => {
        loadOverview();
    }, [loadOverview]);

    const loadUserGrowth = useCallback(async () => {
        setIsUserGrowthLoading(true);
        setUserGrowthError(null);
        try {
            const response = await fetchUserGrowth(months, !includeAdministratorActivity);
            setUserGrowth(response);
        } catch {
            setUserGrowthError('Unable to load user analytics.');
        } finally {
            setIsUserGrowthLoading(false);
        }
    }, [months, includeAdministratorActivity, fetchUserGrowth]);

    const loadSubscriptionFunnel = useCallback(async () => {
        const requestId = subscriptionFunnelRequestRef.current + 1;
        subscriptionFunnelRequestRef.current = requestId;
        setIsSubscriptionFunnelLoading(true);
        setSubscriptionFunnelError(null);
        try {
            const response = await fetchSubscriptionFunnel(Math.min(months, 12), !includeAdministratorActivity);
            if (subscriptionFunnelRequestRef.current === requestId) {
                setSubscriptionFunnel(response);
            }
        } catch {
            if (subscriptionFunnelRequestRef.current === requestId) {
                setSubscriptionFunnelError('Unable to load subscription funnel analytics.');
            }
        } finally {
            if (subscriptionFunnelRequestRef.current === requestId) {
                setIsSubscriptionFunnelLoading(false);
            }
        }
    }, [fetchSubscriptionFunnel, includeAdministratorActivity, months]);

    useEffect(() => {
        if (activeTab === 'users') {
            void Promise.all([loadUserGrowth(), loadSubscriptionFunnel()]);
        }
    }, [activeTab, loadSubscriptionFunnel, loadUserGrowth]);

    const loadAudience = useCallback(async () => {
        if (audienceFrom && audienceTo && audienceFrom > audienceTo) {
            setIsAudienceLoading(false);
            setAudienceError('The to date must be on or after the from date.');
            setSelectedAudienceLocationKeys([]);
            setAudience(null);
            return;
        }

        setIsAudienceLoading(true);
        setAudienceError(null);
        try {
            const response = await fetchAudience(audienceFrom, audienceTo, !includeAdministratorActivity, includeBrowserVersion);
            setSelectedAudienceLocationKeys([]);
            setAudience(response);
        } catch {
            setAudienceError('Unable to load audience analytics.');
        } finally {
            setIsAudienceLoading(false);
        }
    }, [audienceFrom, audienceTo, includeAdministratorActivity, includeBrowserVersion, fetchAudience]);

    useEffect(() => {
        if (activeTab === 'audience') {
            loadAudience();
        }
    }, [activeTab, loadAudience]);

    const refreshActiveAnalytics = useCallback(async () => {
        if (activeTab === 'users') {
            await Promise.all([loadUserGrowth(), loadSubscriptionFunnel()]);
            return;
        }

        if (activeTab === 'audience') {
            await loadAudience();
            return;
        }

        await loadOverview();
    }, [activeTab, loadAudience, loadOverview, loadSubscriptionFunnel, loadUserGrowth]);

    useAutoRefresh(autoRefresh, refreshActiveAnalytics);

    const viewRangeLabel = useMemo(() => {
        if (!overview) {
            return `Last ${days} days`;
        }

        const start = formatShortDate(overview.rangeStartUtc);
        const end = formatShortDate(overview.rangeEndUtc);
        return `${start} - ${end}`;
    }, [overview, days]);

    const userRangeLabel = `Last ${months} months`;

    const renderDailyChart = (title: string, data: AnalyticsDailyCount[] | undefined, highlight?: 'teal' | 'purple') => {
        const safeData = data ?? [];
        if (safeData.length === 0) {
            return (
                <section className={sharedStyles.panel}>
                    <h2>{title}</h2>
                    <p className={sharedStyles.muted}>No data yet.</p>
                </section>
            );
        }

        const max = buildMax(safeData) || 1;
        const highlightClass = highlight ? styles[highlight] ?? '' : '';
        return (
            <section className={sharedStyles.panel}>
                <h2>{title}</h2>
                <div className={styles.chartList}>
                    {safeData.map((item) => (
                        <div key={item.date} className={styles.chartRow}>
                            <span className={styles.chartLabel}>{formatShortDate(item.date)}</span>
                            <div className={styles.chartBar}>
                                <div
                                    className={`${styles.chartFill} ${highlightClass}`}
                                    style={{ width: `${Math.round((item.count / max) * 100)}%` }}
                                />
                            </div>
                            <span className={styles.chartValue}>{item.count}</span>
                        </div>
                    ))}
                </div>
            </section>
        );
    };

    const renderTopVideos = (title: string, data: AnalyticsVideoCount[] | undefined) => {
        const safeData = data ?? [];
        if (safeData.length === 0) {
            return (
                <section className={sharedStyles.panel}>
                    <h2>{title}</h2>
                    <p className={sharedStyles.muted}>No data yet.</p>
                </section>
            );
        }

        const max = buildTopMax(safeData) || 1;
        return (
            <section className={sharedStyles.panel}>
                <h2>{title}</h2>
                <div className={styles.topList}>
                    {safeData.map((item) => (
                        <div key={item.videoId} className={styles.topRow}>
                            <div>
                                <p className={styles.topTitle}>{item.title}</p>
                                <span className={styles.topCount}>{item.count} total</span>
                            </div>
                            <div className={styles.topBar}>
                                <div
                                    className={styles.topFill}
                                    style={{ width: `${Math.round((item.count / max) * 100)}%` }}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </section>
        );
    };

    const renderLabelList = (title: string, data: AnalyticsLabelCount[] | undefined, emptyMessage = 'No data yet.') => {
        const safeData = data ?? [];
        if (safeData.length === 0) {
            return (
                <section className={sharedStyles.panel}>
                    <h2>{title}</h2>
                    <p className={sharedStyles.muted}>{emptyMessage}</p>
                </section>
            );
        }

        const max = safeData.reduce((current, item) => Math.max(current, item.count), 0) || 1;
        return (
            <section className={sharedStyles.panel}>
                <h2>{title}</h2>
                <div className={styles.topList}>
                    {safeData.map((item) => (
                        <div key={item.label} className={styles.topRow}>
                            <div>
                                <p className={styles.topTitle}>{item.label}</p>
                                <span className={styles.topCount}>
                                    {formatNumber(item.count)} total | {formatNumber(item.uniqueUsers)} users
                                </span>
                            </div>
                            <div className={styles.topBar}>
                                <div
                                    className={styles.topFill}
                                    style={{ width: `${Math.round((item.count / max) * 100)}%` }}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </section>
        );
    };

    const renderEventTypeTable = (title: string, data: AnalyticsEventTypeCount[] | undefined) => {
        const safeData = data ?? [];
        return (
            <section className={sharedStyles.panel}>
                <h2>{title}</h2>
                {safeData.length === 0 ? (
                    <p className={sharedStyles.muted}>No product events in this range.</p>
                ) : (
                    <div className={styles.tableWrap}>
                        <table className={sharedStyles.table}>
                            <thead>
                                <tr>
                                    <th>Event</th>
                                    <th>Total</th>
                                    <th>Users</th>
                                </tr>
                            </thead>
                            <tbody>
                                {safeData.map((item) => (
                                    <tr key={item.eventType}>
                                        <td>{formatEventType(item.eventType)}</td>
                                        <td>{formatNumber(item.count)}</td>
                                        <td>{formatNumber(item.uniqueUsers)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        );
    };

    const renderCampaignTable = (data: AnalyticsCampaignCount[] | undefined) => {
        const safeData = data ?? [];
        return (
            <section className={sharedStyles.panel}>
                <h2>Campaign Sources</h2>
                {safeData.length === 0 ? (
                    <p className={sharedStyles.muted}>No campaign attribution in this range.</p>
                ) : (
                    <div className={styles.tableWrap}>
                        <table className={sharedStyles.table}>
                            <thead>
                                <tr>
                                    <th>Source</th>
                                    <th>Medium</th>
                                    <th>Campaign</th>
                                    <th>Events</th>
                                    <th>Users</th>
                                </tr>
                            </thead>
                            <tbody>
                                {safeData.map((item) => (
                                    <tr key={`${item.source}-${item.medium ?? ''}-${item.campaign ?? ''}`}>
                                        <td>{item.source}</td>
                                        <td>{item.medium ?? '-'}</td>
                                        <td>{item.campaign ?? '-'}</td>
                                        <td>{formatNumber(item.count)}</td>
                                        <td>{formatNumber(item.uniqueUsers)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        );
    };

    const renderConversionTable = (data: AdvertisingConversionSummary[] | undefined) => {
        const safeData = data ?? [];
        return (
            <section className={sharedStyles.panel}>
                <h2>Advertising Conversion Outbox</h2>
                {safeData.length === 0 ? (
                    <p className={sharedStyles.muted}>No advertising conversions in this range.</p>
                ) : (
                    <div className={styles.tableWrap}>
                        <table className={sharedStyles.table}>
                            <thead>
                                <tr>
                                    <th>Provider</th>
                                    <th>Event</th>
                                    <th>Status</th>
                                    <th>Count</th>
                                </tr>
                            </thead>
                            <tbody>
                                {safeData.map((item) => (
                                    <tr key={`${item.provider}-${item.eventName}-${item.status}`}>
                                        <td>{item.provider}</td>
                                        <td>{item.eventName}</td>
                                        <td>{item.status}</td>
                                        <td>{formatNumber(item.count)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        );
    };

    const renderRecentActivityTable = (data: AnalyticsRecentActivity[] | undefined) => {
        const safeData = data ?? [];
        return (
            <section className={sharedStyles.panel}>
                <h2>Recent Product Activity</h2>
                {safeData.length === 0 ? (
                    <p className={sharedStyles.muted}>No product activity yet.</p>
                ) : (
                    <div className={styles.tableWrap}>
                        <table className={sharedStyles.table}>
                            <thead>
                                <tr>
                                    <th>When</th>
                                    <th>Event</th>
                                    <th>User</th>
                                    <th>Video</th>
                                    <th>Summary</th>
                                </tr>
                            </thead>
                            <tbody>
                                {safeData.map((item) => (
                                    <tr key={`${item.eventType}-${item.occurredAtUtc}-${item.userName ?? ''}`}>
                                        <td>{formatDateTime(item.occurredAtUtc)}</td>
                                        <td>{formatEventType(item.eventType)}</td>
                                        <td>{item.userName ?? 'Anonymous'}</td>
                                        <td>{item.videoTitle ?? '-'}</td>
                                        <td>{item.summary ?? item.path ?? '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        );
    };

    const renderOverviewTab = () => {
        if (isOverviewLoading) {
            return <div className={sharedStyles.muted}>Loading analytics...</div>;
        }

        if (overviewError) {
            return <div className={sharedStyles.muted}>{overviewError}</div>;
        }

        if (!overview) {
            return null;
        }

        return (
            <>
                <p className={styles.rangeLabel}>{viewRangeLabel}</p>
                <div className={styles.grid}>
                    {renderDailyChart('Page Views (Signed In)', overview.pageViewsSignedIn)}
                    {renderDailyChart('Page Views (Signed Out)', overview.pageViewsSignedOut, 'purple')}
                </div>
                <div className={styles.grid}>
                    {renderTopVideos('Top Watched Videos', overview.topWatchedVideos)}
                    {renderTopVideos('Top Saved Videos', overview.topSavedVideos)}
                    {renderTopVideos('Top Purchased Videos', overview.topPurchasedVideos)}
                </div>
            </>
        );
    };

    const renderUserGrowthChart = (growth: AnalyticsUserGrowth) => {
        const safeData = growth.monthlyGrowth ?? [];
        if (safeData.length === 0) {
            return (
                <section className={sharedStyles.panel}>
                    <h2>Monthly New Users</h2>
                    <p className={sharedStyles.muted}>No user growth data yet.</p>
                </section>
            );
        }

        const max = buildGrowthMax(safeData) || 1;
        return (
            <section className={sharedStyles.panel}>
                <div className={styles.panelHeading}>
                    <h2>Monthly New Users</h2>
                    <div className={styles.legend}>
                        <span><i className={styles.legendMember} aria-hidden="true" />Members</span>
                        <span><i className={styles.legendSubscribed} aria-hidden="true" />Subscribed</span>
                        <span><i className={styles.legendPending} aria-hidden="true" />Pending Unsubscribed</span>
                        <span><i className={styles.legendUnsubscribed} aria-hidden="true" />Unsubscribed</span>
                    </div>
                </div>
                <div className={styles.growthList}>
                    {safeData.map((item) => {
                        const total = item.newUsers || 1;
                        const monthLabel = formatMonth(item.monthStart);
                        const barWidth = Math.max(4, Math.round((item.newUsers / max) * 100));
                        const segments = [
                            { label: 'Members', value: item.newMembers, className: styles.segmentMember },
                            { label: 'Subscribed', value: item.newSubscribed, className: styles.segmentSubscribed },
                            { label: 'Pending Unsubscribed', value: item.newPendingUnsubscribed, className: styles.segmentPending },
                            { label: 'Unsubscribed', value: item.newUnsubscribed, className: styles.segmentUnsubscribed }
                        ];
                        const activeSegment = segments.find((segment) => activeGrowthTooltip === `${item.monthStart}:${segment.label}`);
                        const activeTooltip = activeSegment
                            ? buildGrowthTooltip(monthLabel, activeSegment.label, activeSegment.value, item.newUsers)
                            : null;
                        const tooltipId = `growth-tooltip-${item.monthStart.replace(/\W/g, '-')}`;
                        return (
                            <div key={item.monthStart} className={styles.growthRow}>
                                <span className={styles.chartLabel}>{monthLabel}</span>
                                <div className={styles.growthTrack}>
                                    <div className={styles.growthStack} style={{ width: `${barWidth}%` }}>
                                        {segments.map((segment) => {
                                            const tooltipKey = `${item.monthStart}:${segment.label}`;
                                            const tooltip = buildGrowthTooltip(monthLabel, segment.label, segment.value, item.newUsers);
                                            return (
                                                <span
                                                    key={segment.label}
                                                    aria-describedby={activeGrowthTooltip === tooltipKey ? tooltipId : undefined}
                                                    aria-label={tooltip}
                                                    className={`${styles.growthSegment} ${segment.className}`}
                                                    onBlur={() =>
                                                        setActiveGrowthTooltip((current) => (current === tooltipKey ? null : current))
                                                    }
                                                    onFocus={() => setActiveGrowthTooltip(tooltipKey)}
                                                    onMouseEnter={() => setActiveGrowthTooltip(tooltipKey)}
                                                    onMouseLeave={() =>
                                                        setActiveGrowthTooltip((current) => (current === tooltipKey ? null : current))
                                                    }
                                                    role="img"
                                                    style={{ width: `${Math.round((segment.value / total) * 100)}%` }}
                                                    tabIndex={segment.value > 0 ? 0 : -1}
                                                    title={tooltip}
                                                />
                                            );
                                        })}
                                    </div>
                                    {activeTooltip ? (
                                        <span className={styles.growthTooltip} id={tooltipId} role="tooltip">
                                            {activeTooltip}
                                        </span>
                                    ) : null}
                                </div>
                                <span className={styles.chartValue}>{item.newUsers}</span>
                            </div>
                        );
                    })}
                </div>
            </section>
        );
    };

    const renderSubscriptionFunnel = (funnel: SubscriptionFunnelAnalytics) => {
        const { summary } = funnel;
        const maxStageCount = funnel.stages.reduce((max, stage) => Math.max(max, stage.count), 0) || 1;
        const maxFailureCount = funnel.failureReasons.reduce((max, reason) => Math.max(max, reason.count), 0) || 1;

        return (
            <div className={styles.funnelSection}>
                <div className={styles.panelHeading}>
                    <div>
                        <h2 className={styles.sectionTitle}>Subscription funnel</h2>
                        <p className={styles.rangeLabel}>
                            Conversion uses customers old enough to complete the {funnel.completionWindowHours || 24}-hour window.
                            {months > 12 ? ' Funnel reporting is limited to the last 12 months.' : ''}
                        </p>
                    </div>
                </div>

                <section className={sharedStyles.panel}>
                    <h2>Pay-and-play performance</h2>
                    <div className={styles.metricGrid}>
                        <div className={styles.metricItem}>
                            <span>Checkout views</span>
                            <strong>{formatNumber(summary.checkoutViews)}</strong>
                            <small>{formatNumber(summary.eligibleCheckoutViews)} eligible for 24h conversion</small>
                        </div>
                        <div className={styles.metricItem}>
                            <span>Accounts ready</span>
                            <strong>{formatNumber(summary.accountsReady)}</strong>
                            <small>{formatNumber(summary.eligibleAccountsReady)} eligible for 24h conversion</small>
                        </div>
                        <div className={styles.metricItem}>
                            <span>Checkouts reaching payment</span>
                            <strong>{formatNumber(summary.checkoutsReachingPayment)}</strong>
                            <small>Unique authenticated checkouts at the payment stage</small>
                        </div>
                        <div className={styles.metricItem}>
                            <span>Completed subscriptions</span>
                            <strong>{formatNumber(summary.completed)}</strong>
                            <small>{formatNumber(summary.completedWithin24Hours)} completed within 24 hours</small>
                        </div>
                        <div className={styles.metricItem}>
                            <span>Checkout conversion</span>
                            <strong>{summary.checkoutConversionRate.toFixed(1)}%</strong>
                            <small>Mature checkout views paid within 24h</small>
                        </div>
                        <div className={styles.metricItem}>
                            <span>Account conversion</span>
                            <strong>{summary.accountConversionRate.toFixed(1)}%</strong>
                            <small>Ready or authenticated accounts paid within 24h</small>
                        </div>
                    </div>
                </section>

                <div className={styles.grid}>
                    <section className={sharedStyles.panel}>
                        <h2>Journey stages</h2>
                        {funnel.stages.length === 0 ? (
                            <p className={sharedStyles.muted}>No funnel activity in this range.</p>
                        ) : (
                            <div className={styles.funnelStages}>
                                {funnel.stages.map((stage, index) => (
                                    <div className={styles.funnelStage} key={stage.stage}>
                                        <div>
                                            <strong>{formatEventType(stage.stage)}</strong>
                                            <span>{formatNumber(stage.count)} checkouts · {formatNumber(stage.uniqueUsers)} users</span>
                                        </div>
                                        <div className={styles.funnelTrack} aria-hidden="true">
                                            <span style={{ width: `${Math.max(2, (stage.count / maxStageCount) * 100)}%` }} />
                                        </div>
                                        <small>{index === 0 ? 'Entry stage' : `${stage.conversionFromPrevious.toFixed(1)}% from previous`}</small>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>

                    <section className={sharedStyles.panel}>
                        <h2>Why customers did not complete</h2>
                        {funnel.failureReasons.length === 0 ? (
                            <p className={sharedStyles.muted}>No known failures in this range.</p>
                        ) : (
                            <div className={styles.failureList}>
                                {funnel.failureReasons.map((reason) => (
                                    <div className={styles.failureRow} key={reason.failureCategory}>
                                        <div>
                                            <strong>{formatEventType(reason.failureCategory)}</strong>
                                            <span>{formatNumber(reason.count)} · {reason.percentage.toFixed(1)}%</span>
                                        </div>
                                        <div className={styles.failureTrack} aria-hidden="true">
                                            <span style={{ width: `${Math.max(2, (reason.count / maxFailureCount) * 100)}%` }} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>
                </div>

            </div>
        );
    };

    const renderUsersTab = () => {
        if (isUserGrowthLoading && isSubscriptionFunnelLoading && !userGrowth && !subscriptionFunnel) {
            return <div className={sharedStyles.muted}>Loading user analytics...</div>;
        }

        const totals = userGrowth?.totals;
        return (
            <>
                <p className={styles.rangeLabel}>{userRangeLabel}</p>
                {subscriptionFunnelError ? <div className={sharedStyles.muted}>{subscriptionFunnelError}</div> : null}
                {isSubscriptionFunnelLoading && !subscriptionFunnel ? <div className={sharedStyles.muted}>Loading subscription funnel…</div> : null}
                {subscriptionFunnel ? renderSubscriptionFunnel(subscriptionFunnel) : null}
                {userGrowthError ? <div className={sharedStyles.muted}>{userGrowthError}</div> : null}
                {totals ? (
                    <section className={sharedStyles.panel}>
                        <h2>User totals</h2>
                        <div className={styles.metricGrid}>
                            <div className={styles.metricItem}><span>Total users</span><strong>{formatNumber(totals.totalUsers)}</strong></div>
                            <div className={styles.metricItem}><span>Enabled users</span><strong>{formatNumber(totals.totalEnabledUsers)}</strong></div>
                            <div className={styles.metricItem}><span>Members</span><strong>{formatNumber(totals.totalMembers)}</strong></div>
                            <div className={styles.metricItem}><span>Subscribers</span><strong>{formatNumber(totals.totalSubscribers)}</strong></div>
                            <div className={styles.metricItem}><span>Pending unsubscribed</span><strong>{formatNumber(totals.totalPendingUnsubscribed)}</strong></div>
                            <div className={styles.metricItem}><span>Unsubscribed</span><strong>{formatNumber(totals.totalUnsubscribed)}</strong></div>
                        </div>
                    </section>
                ) : null}
                {userGrowth ? renderUserGrowthChart(userGrowth) : null}
            </>
        );
    };

    const renderVideoTab = () => {
        if (isOverviewLoading) {
            return <div className={sharedStyles.muted}>Loading analytics...</div>;
        }

        if (overviewError) {
            return <div className={sharedStyles.muted}>{overviewError}</div>;
        }

        if (!overview) {
            return null;
        }

        return (
            <>
                <p className={styles.rangeLabel}>{viewRangeLabel}</p>
                <div className={styles.grid}>
                    {renderDailyChart('Video Watch Starts', overview.videoWatchStarts)}
                    {renderDailyChart('Video Saves', overview.videoSaves, 'teal')}
                    {renderDailyChart('Video Purchases', overview.videoPurchases, 'purple')}
                </div>
                <div className={styles.grid}>
                    {renderTopVideos('Top Watched Videos', overview.topWatchedVideos)}
                    {renderTopVideos('Top Saved Videos', overview.topSavedVideos)}
                    {renderTopVideos('Top Purchased Videos', overview.topPurchasedVideos)}
                </div>
                <section className={sharedStyles.panel}>
                    <h2>Recent Video Watches</h2>
                    {(overview.recentVideoWatches ?? []).length === 0 ? (
                        <p className={sharedStyles.muted}>No watch events yet.</p>
                    ) : (
                        <div className={styles.tableWrap}>
                            <table className={sharedStyles.table}>
                                <thead>
                                    <tr>
                                        <th>When</th>
                                        <th>Video</th>
                                        <th>User</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(overview.recentVideoWatches ?? []).map((item) => (
                                        <tr key={`${item.videoId}-${item.occurredAtUtc}`}>
                                            <td>{formatDateTime(item.occurredAtUtc)}</td>
                                            <td>{item.title}</td>
                                            <td>{item.userName ?? 'Unknown'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>
            </>
        );
    };

    const renderPlaybackTab = () => {
        if (isOverviewLoading) {
            return <div className={sharedStyles.muted}>Loading analytics...</div>;
        }

        if (overviewError) {
            return <div className={sharedStyles.muted}>{overviewError}</div>;
        }

        if (!overview) {
            return null;
        }

        return (
            <PlaybackPerformancePanel
                performance={overview.playbackPerformance}
                rangeLabel={viewRangeLabel}
            />
        );
    };

    const renderDiscoveryTab = () => {
        if (isOverviewLoading) {
            return <div className={sharedStyles.muted}>Loading analytics...</div>;
        }

        if (overviewError) {
            return <div className={sharedStyles.muted}>{overviewError}</div>;
        }

        if (!overview) {
            return null;
        }

        return (
            <>
                <p className={styles.rangeLabel}>{viewRangeLabel}</p>
                <div className={styles.grid}>
                    {renderDailyChart('Searches', overview.searches)}
                    {renderDailyChart('Search Result Clicks', overview.searchResultClicks, 'teal')}
                    {renderDailyChart('Recommendation Clicks', overview.recommendationClicks, 'purple')}
                </div>
                <div className={styles.grid}>
                    {renderLabelList('Top Search Terms', overview.topSearchTerms, 'No tracked search terms yet.')}
                    {renderLabelList('Top Interests', overview.topInterests, 'No clicked tags, fighters, events, or collections yet.')}
                </div>
                {renderEventTypeTable('Product Event Counts', overview.eventTypeCounts)}
            </>
        );
    };

    const renderRetentionTab = () => {
        if (isOverviewLoading) {
            return <div className={sharedStyles.muted}>Loading analytics...</div>;
        }

        if (overviewError) {
            return <div className={sharedStyles.muted}>{overviewError}</div>;
        }

        if (!overview) {
            return null;
        }

        const retention = overview.retention;
        return (
            <>
                <p className={styles.rangeLabel}>{viewRangeLabel}</p>
                <section className={sharedStyles.panel}>
                    <h2>Retention Summary</h2>
                    <div className={styles.metricGrid}>
                        <div className={styles.metricItem}>
                            <span>Active Users</span>
                            <strong>{formatNumber(retention.activeUsers)}</strong>
                        </div>
                        <div className={styles.metricItem}>
                            <span>New Users</span>
                            <strong>{formatNumber(retention.newUsers)}</strong>
                        </div>
                        <div className={styles.metricItem}>
                            <span>Returning Users</span>
                            <strong>{formatNumber(retention.returningUsers)}</strong>
                        </div>
                        <div className={styles.metricItem}>
                            <span>Repeat Users</span>
                            <strong>{formatNumber(retention.repeatUsers)}</strong>
                        </div>
                        <div className={styles.metricItem}>
                            <span>Avg Active Days</span>
                            <strong>{retention.averageActiveDays.toLocaleString()}</strong>
                        </div>
                    </div>
                </section>
                {renderDailyChart('Daily Active Users', retention.dailyActiveUsers, 'teal')}
            </>
        );
    };

    const renderAdvertisingTab = () => {
        if (isOverviewLoading) {
            return <div className={sharedStyles.muted}>Loading analytics...</div>;
        }

        if (overviewError) {
            return <div className={sharedStyles.muted}>{overviewError}</div>;
        }

        if (!overview) {
            return null;
        }

        return (
            <>
                <p className={styles.rangeLabel}>{viewRangeLabel}</p>
                <div className={styles.grid}>
                    {renderDailyChart('Signups', overview.signups)}
                    {renderDailyChart('Subscription Starts', overview.subscriptionStarts, 'teal')}
                    {renderDailyChart('Subscription Cancellations', overview.subscriptionCancellations, 'purple')}
                </div>
                {renderCampaignTable(overview.topCampaigns)}
                {renderConversionTable(overview.advertisingConversions)}
            </>
        );
    };

    const renderActivityTab = () => {
        if (isOverviewLoading) {
            return <div className={sharedStyles.muted}>Loading analytics...</div>;
        }

        if (overviewError) {
            return <div className={sharedStyles.muted}>{overviewError}</div>;
        }

        if (!overview) {
            return null;
        }

        return (
            <>
                <p className={styles.rangeLabel}>{viewRangeLabel}</p>
                {renderRecentActivityTable(overview.recentActivity)}
            </>
        );
    };

    const renderLoginsTab = () => {
        if (isOverviewLoading) {
            return <div className={sharedStyles.muted}>Loading analytics...</div>;
        }

        if (overviewError) {
            return <div className={sharedStyles.muted}>{overviewError}</div>;
        }

        if (!overview) {
            return null;
        }

        return (
            <section className={sharedStyles.panel}>
                <h2>Recent Logins</h2>
                {(overview.recentLogins ?? []).length === 0 ? (
                    <p className={sharedStyles.muted}>No recent logins.</p>
                ) : (
                    <div className={styles.tableWrap}>
                        <table className={sharedStyles.table}>
                            <thead>
                                <tr>
                                    <th>When</th>
                                    <th>User</th>
                                    <th>Status</th>
                                    <th>IP</th>
                                    <th>Device</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(overview.recentLogins ?? []).map((item) => (
                                    <tr key={`${item.userName ?? 'unknown'}-${item.occurredAtUtc}`}>
                                        <td>{formatDateTime(item.occurredAtUtc)}</td>
                                        <td>{item.userName ?? 'Unknown'}</td>
                                        <td className={item.isSuccess ? styles.statusSuccess : styles.statusFail}>
                                            {item.isSuccess ? 'Success' : 'Failed'}
                                        </td>
                                        <td>{item.ipAddress ?? '-'}</td>
                                        <td className={styles.agent}>{item.userAgent ?? '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        );
    };

    const renderAudienceMap = (audienceData: AnalyticsAudience) => {
        const locations = audienceData.locations ?? [];
        const mapLocations = buildAudienceMapLocations(locations);
        const selectedKeySet = new Set(selectedAudienceLocationKeys);
        const visibleLocations = selectedAudienceLocationKeys.length === 0
            ? mapLocations
            : mapLocations.filter((location) => selectedKeySet.has(location.key));
        const markers: GeoPointMarker[] = visibleLocations.map((location) => {
            const loginLabel = `${formatNumber(location.count)} ${location.count === 1 ? 'login' : 'logins'}`;
            const uniqueUserLabel = `${formatNumber(location.uniqueUsers)} ${location.uniqueUsers === 1 ? 'unique user' : 'unique users'}`;
            const details = location.sourceCount > 1
                ? `${uniqueUserLabel}; ${formatNumber(location.sourceCount)} source locations merged`
                : uniqueUserLabel;

            return {
                id: `login-location-${location.key}`,
                label: location.label,
                latitude: location.latitude,
                longitude: location.longitude,
                variant: 'login',
                description: loginLabel,
                details,
                pinCount: location.count
            };
        });
        const toggleLocationSelection = (key: string) => {
            setSelectedAudienceLocationKeys((current) =>
                current.includes(key)
                    ? current.filter((item) => item !== key)
                    : [...current, key]
            );
        };

        return (
            <section className={sharedStyles.panel}>
                <div className={styles.panelHeading}>
                    <h2>User Locations</h2>
                    <div className={styles.mapActions}>
                        <span className={styles.mapMeta}>{formatNumber(audienceData.unknownLocationCount)} unresolved logins</span>
                        {selectedAudienceLocationKeys.length > 0 ? (
                            <button
                                type="button"
                                className={styles.mapClearButton}
                                onClick={() => setSelectedAudienceLocationKeys([])}
                            >
                                Clear selection
                            </button>
                        ) : null}
                    </div>
                </div>
                {locations.length === 0 ? (
                    <p className={sharedStyles.muted}>No resolved locations in this range.</p>
                ) : (
                    <>
                        <GeoPointMap
                            ariaLabel="Login location map"
                            emptyMessage="No mapped coordinates found for this range."
                            markers={markers}
                        />
                        <div className={styles.locationList}>
                            {mapLocations.map((location) => {
                                const isSelected = selectedKeySet.has(location.key);
                                return (
                                    <label
                                        key={location.key}
                                        className={`${styles.locationRow} ${isSelected ? styles.locationRowSelected : ''}`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => toggleLocationSelection(location.key)}
                                        />
                                        <span>{location.label}</span>
                                        <strong>{formatNumber(location.count)}</strong>
                                    </label>
                                );
                            })}
                        </div>
                    </>
                )}
            </section>
        );
    };

    const renderAudienceTab = () => {
        if (isAudienceLoading) {
            return <div className={sharedStyles.muted}>Loading audience analytics...</div>;
        }

        if (audienceError) {
            return <div className={sharedStyles.muted}>{audienceError}</div>;
        }

        if (!audience) {
            return null;
        }

        const devices = audience.devices ?? [];

        return (
            <>
                <p className={styles.rangeLabel}>{buildAudienceRangeLabel(audience)}</p>
                <section className={sharedStyles.panel}>
                    <h2>Login Audience</h2>
                    <div className={styles.metricGrid}>
                        <div className={styles.metricItem}>
                            <span>Total Logins</span>
                            <strong>{formatNumber(audience.totalLogins)}</strong>
                        </div>
                        <div className={styles.metricItem}>
                            <span>Successful</span>
                            <strong>{formatNumber(audience.successfulLogins)}</strong>
                        </div>
                        <div className={styles.metricItem}>
                            <span>Failed</span>
                            <strong>{formatNumber(audience.failedLogins)}</strong>
                        </div>
                        <div className={styles.metricItem}>
                            <span>Unique Users</span>
                            <strong>{formatNumber(audience.uniqueUsers)}</strong>
                        </div>
                    </div>
                </section>
                {renderAudienceMap(audience)}
                <section className={sharedStyles.panel}>
                    <h2>Devices</h2>
                    {devices.length === 0 ? (
                        <p className={sharedStyles.muted}>No device data in this range.</p>
                    ) : (
                        <div className={styles.tableWrap}>
                            <table className={sharedStyles.table}>
                                <thead>
                                    <tr>
                                        <th>Device</th>
                                        <th>OS</th>
                                        <th>OS Version</th>
                                        <th>Browser</th>
                                        {includeBrowserVersion ? <th>Browser Version</th> : null}
                                        <th>Logins</th>
                                        <th>Users</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {devices.map((device, index) => (
                                        <tr key={buildDeviceBucketKey(device, includeBrowserVersion, index)}>
                                            <td>{device.deviceType}</td>
                                            <td>{device.operatingSystem}</td>
                                            <td>{device.operatingSystemVersion ?? '-'}</td>
                                            <td>{device.browser}</td>
                                            {includeBrowserVersion ? <td>{device.browserVersion ?? '-'}</td> : null}
                                            <td>{formatNumber(device.count)}</td>
                                            <td>{formatNumber(device.uniqueUsers)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>
            </>
        );
    };

    const renderFilters = () => {
        const adminUsersToggle = (
            <label className={styles.toggleLabel}>
                <input
                    type="checkbox"
                    checked={includeAdministratorActivity}
                    onChange={(event) => setIncludeAdministratorActivity(event.target.checked)}
                />
                Include Admin/SuperAdmin activity
            </label>
        );

        if (activeTab === 'users') {
            return (
                <>
                    <label className={styles.filterLabel}>
                        Months
                        <select value={months} onChange={(event) => setMonths(Number(event.target.value))}>
                            {monthOptions.map((value) => (
                                <option key={value} value={value}>{value}</option>
                            ))}
                        </select>
                    </label>
                    {adminUsersToggle}
                </>
            );
        }

        if (activeTab === 'audience') {
            return (
                <>
                    <label className={styles.filterLabel}>
                        From
                        <input
                            type="date"
                            value={audienceFrom}
                            onChange={(event) => setAudienceFrom(event.target.value)}
                        />
                    </label>
                    <label className={styles.filterLabel}>
                        To
                        <input
                            type="date"
                            value={audienceTo}
                            onChange={(event) => setAudienceTo(event.target.value)}
                        />
                    </label>
                    {adminUsersToggle}
                    <label className={styles.toggleLabel}>
                        <input
                            type="checkbox"
                            checked={includeBrowserVersion}
                            onChange={(event) => setIncludeBrowserVersion(event.target.checked)}
                        />
                        Split by browser version
                    </label>
                </>
            );
        }

        return (
            <>
                {activeTab !== 'logins' ? (
                    <>
                        <label className={styles.filterLabel}>
                            Range
                            <select value={days} onChange={(event) => setDays(Number(event.target.value))}>
                                {dayOptions.map((value) => (
                                    <option key={value} value={value}>{value} days</option>
                                ))}
                            </select>
                        </label>
                        <label className={styles.filterLabel}>
                            Top
                            <select value={top} onChange={(event) => setTop(Number(event.target.value))}>
                                {topOptions.map((value) => (
                                    <option key={value} value={value}>{value}</option>
                                ))}
                            </select>
                        </label>
                    </>
                ) : null}
                {adminUsersToggle}
            </>
        );
    };

    const renderActiveTab = () => {
        switch (activeTab) {
            case 'users':
                return renderUsersTab();
            case 'video':
                return renderVideoTab();
            case 'playback':
                return renderPlaybackTab();
            case 'discovery':
                return renderDiscoveryTab();
            case 'retention':
                return renderRetentionTab();
            case 'advertising':
                return renderAdvertisingTab();
            case 'activity':
                return renderActivityTab();
            case 'logins':
                return renderLoginsTab();
            case 'audience':
                return renderAudienceTab();
            default:
                return renderOverviewTab();
        }
    };

    return (
        <div className={sharedStyles.page}>
            <header className={sharedStyles.header}>
                <div>
                    <h1>Analytics</h1>
                    <p>Track signed-in activity, content usage, conversions, and user growth.</p>
                </div>
                <div className={styles.filters}>
                    {renderFilters()}
                    <AutoRefreshControl checked={autoRefresh} onChange={setAutoRefresh} />
                </div>
            </header>

            <div className={styles.tabs} role="tablist" aria-label="Analytics sections">
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        type="button"
                        role="tab"
                        aria-selected={activeTab === tab.id}
                        className={activeTab === tab.id ? styles.activeTab : styles.tab}
                        onClick={() => setActiveTab(tab.id)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {renderActiveTab()}
        </div>
    );
};

export default AdminAnalytics;
